import { ComboboxItem, Select, TextInput } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { Filter } from 'common/repositories/common.model';
import { toISOString } from 'common/utils/date';
import { capitalize } from 'common/utils/string';
import {
  DateRangeEnum,
  afterBeforeHandler,
} from 'components/common/query-filter/utils';
import useFilterHandleComponent from 'hooks/filter/use-filter-handle-component';
import React from 'react';
import AgentSelect from 'templates/raw-select/agent-select';
import BankAccountSelect from 'templates/raw-select/bank-account-select';
import BonusPeriodSelect from 'templates/raw-select/bonus-period-select';
import CashAccountSelect from 'templates/raw-select/cash-account-select';
import CustomerSelect from 'templates/raw-select/customer-select';
import EmployeeSelect from 'templates/raw-select/employee-select';
import GuestSelect from 'templates/raw-select/guest-select';
import { SelectTypeEnum } from 'templates/raw-select/raw-select.constant';
import SaleTypeSelect from 'templates/raw-select/sale-type-select';

interface UniskaiFilterInputProps {
  filter: Filter;
}

// just set for client side
export default function UniskaiFilterInput(props: UniskaiFilterInputProps) {
  const { filter } = props;

  const clientFilterHandler = useFilterHandleComponent();
  const clientFilter = React.useMemo(() => {
    return clientFilterHandler.filters.find((f) => f.name === filter.name);
  }, [clientFilterHandler.filters, filter.name]);

  const value = React.useMemo(() => {
    if (clientFilter?.value) return clientFilter.value;
    if (typeof filter.value === 'string') return filter.value;
    if (typeof filter.default === 'string') return filter.default;
    return undefined;
  }, [clientFilter?.value, filter.default, filter.value]);

  const options: ComboboxItem[] = (filter.options || []).map((option) => {
    const id = option.value ?? option;
    return {
      label: capitalize(option.label ?? option),
      extra: option,
      value: typeof id === 'string' ? id : `${id}`,
    };
  });

  const SelectAPI = React.useMemo(() => {
    if (clientFilterHandler.isUseResponseOptions) return null;
    switch (filter.name) {
      case SelectTypeEnum.operator:
      case SelectTypeEnum.sales:
      case SelectTypeEnum.lead:
      case SelectTypeEnum.admin:
      case SelectTypeEnum.document_admin:
      case SelectTypeEnum.employee:
      case SelectTypeEnum.employee_id:
        return EmployeeSelect;
      case SelectTypeEnum.agent:
        return AgentSelect;
      case SelectTypeEnum.bank_account:
        return BankAccountSelect;
      case SelectTypeEnum.bonus_period:
        return BonusPeriodSelect;
      case SelectTypeEnum.cash_account:
        return CashAccountSelect;
      case SelectTypeEnum.customer:
        return CustomerSelect;
      case SelectTypeEnum.guest:
        return GuestSelect;
      case SelectTypeEnum.type:
        return SaleTypeSelect;
    }
    return null;
  }, [clientFilterHandler.isUseResponseOptions, filter.name]);

  if (filter.name.includes(DateRangeEnum.atBefore)) return null;

  if (filter.name.includes(DateRangeEnum.atAfter)) {
    const { atBeforeName, filterLabel, transformerChange, value } =
      afterBeforeHandler(
        filter,
        clientFilterHandler.filters,
        clientFilter?.value || undefined,
      );

    return (
      <DatePickerInput
        size="xs"
        type="range"
        label={filterLabel}
        placeholder="DD-MM-YYYY - DD-MM-YYYY"
        value={value}
        clearable
        onChange={(val) => {
          if (!Array.isArray(val)) return;
          const { atAfter, atBefore } = transformerChange(
            val as DatesRangeValue,
          );
          clientFilterHandler.onChangeFilter(filter.name, atAfter);
          clientFilterHandler.onChangeFilter(atBeforeName, atBefore);
        }}
      />
    );
  }

  if (SelectAPI) {
    return (
      <SelectAPI
        label={filter.label}
        value={value}
        onChange={(val) => {
          clientFilterHandler.onChangeFilter(filter.name, val);
        }}
      />
    );
  }

  switch (filter.type) {
    case 'text':
      return (
        <TextInput
          label={filter.label}
          onChange={(e) => {
            clientFilterHandler.onChangeFilter(filter.name, e.target.value);
          }}
          value={value || ''}
        />
      );

    case 'date':
      return (
        <DatePickerInput
          type="default"
          label={filter.label}
          clearable
          onChange={(val: Date | null) => {
            if (val === null) {
              clientFilterHandler.onChangeFilter(filter.name, undefined);
            } else {
              clientFilterHandler.onChangeFilter(filter.name, toISOString(val));
            }
          }}
          value={value ? new Date(value) : undefined}
        />
      );
    case 'option':
      return (
        <Select
          label={filter.label}
          placeholder={filter.label}
          value={value}
          data={options}
          onChange={(val) => {
            clientFilterHandler.onChangeFilter(filter.name, val);
          }}
        />
      );
    default:
      return <>{filter.type}</>;
  }
}
